// Show me: a companion does one of its moves now, once, so the owner can see what it does. It's the
// one exception to "companions act on their own" (the owner, 2026-11-01): everything else they
// still decide themselves. Every action a companion can do has one, whatever its switch says, and
// so do its attacks (a pest of each kind it meets, a duel, turning evil), walking and hopping, going
// home and coming out (where the world has a home) and the games. Keys are stable strings without
// interface text: the menu has their labels. runtime/README.md documents the API:
//
//   list(id)                → [{ key, action, partner? }]: action is the catalog action it belongs
//                             to (catalog.js; null for walking and going home), partner: true when it
//                             needs another companion the owner may pick (a duel's foe)
//   partners(id, key)       → the ids that could take part in one with a partner, best first: on
//                             screen and free, then on screen, then the others
//   partner(id, key)        → the one it takes when none is asked for, also the one an own thing of
//                             its needs (Rudolph pulls Santa's sleigh), or null
//   run(id, key, { with })  → resolves once the move is over: true when it played, false when it
//                             couldn't (no such key, nobody to take part, the runtime not running).
//                             `elsewhere`: ids on screen in another runtime (the desktop's other
//                             monitors), never brought here as visitors; `puff`: visitors leave in a
//                             puff instead of walking off (a host whose companions hold still)
//
// It interrupts whatever the companion is doing and plays while Alive is off and at Still too
// (activity.js). One that isn't on screen comes for it as a visitor, and so does a partner it needs:
// a visit is no change of who is on screen (runtime/index.js), and once the move is over the
// visitor leaves again its own way, unless it was put on screen meanwhile. While it plays, the
// companions in it take part in nothing else (index.js: joins).
import { clockNow } from '../engine/clock.js';
import { env, rand, tick } from '../engine/core.js';
import { actionsFor, possible, can, ownTravel, ownIdle } from '../engine/catalog.js';
import { ability } from '../engine/abilities.js';
import { applyRest } from '../engine/companion.js';
import { FLOOR, floorY, castleLeft, home } from '../engine/world.js';
import { travelTo, baseOf, generalWay } from '../engine/travel.js';
import * as acts from '../engine/acts.js';
import * as together from '../engine/together.js';
import * as fights from '../engine/fights.js';
import { play as playWithPointer } from '../engine/pointer.js';
import { leaveOver } from './edges.js';

// Who can be the partner in the games and fights that need one: the part that other one plays.
const ROLES = {
  'fight.duel': 'fight.duel',             // a duel: another that fights
  'basketball.throw': 'basketball.catch', // shooting: a catcher
  'basketball.catch': 'basketball.throw', // catching: a shooter
  'party.join': 'party.host',             // joining a party: one who throws it
};
const PARTNER_WAIT = 6000;                // ms a friend has to get ready for an own thing done with it (one walking in needs a few seconds)
const NAP = 12000;                        // ms of a nap (a click wakes it sooner)
const LONGEST = 5 * 60000;                // ms after which a Show me counts as over, whatever happens

// rt: what the runtime lends it (index.js): all, cast, order (every id the host can bring, in menu
// order), known(id), ready(ids), companion(id), started(), destroyed(), run, runPair, startParty,
// turnEvil, visit(me), visiting(me), dismiss(me, { puff }), free(me), joins(me, asker), emit.
export function createShowMe(rt) {
  const sessions = new Map();             // companion -> the Show me it is in (the one it's of, or a partner)
  const waiting = new Set();              // promises waiting for a frame condition

  // ---------- what there is to show ----------
  function list(id) {
    const me = rt.companion(id), keys = [];
    const add = (key, action) => keys.push(ROLES[key] ? { key, action, partner: true } : { key, action });
    add('near', null); add('far', null);
    for (const a of actionsFor(me.design)) {
      if (a.id === 'pests') { add('pests:bug', 'pests'); add('pests:glitch', 'pests'); } else add(a.id, a.id);
    }
    if (home()) { add('home', null); add('out', null); }
    return keys;
  }
  const has = (me, key) => list(me.design.id).some(e => e.key === key);

  // The ids of those that could take part, best first. For a game or a fight, those that could play
  // the other part: on screen and free (and taking part in things), then on screen, then the
  // others; among equals, those whose switch says they play it, in menu order. Only those whose
  // designs are here (a host that brings them later readies every design before its menu shows
  // them). For an own thing done with a particular friend (an ability's `partners`: Rudolph and
  // Santa), those friends, the ones on screen first.
  const onScreen = id => rt.cast.includes(rt.all[id]);
  function candidates(me, key) {
    const role = ROLES[key];
    if (role) {
      const rank = o => (rt.cast.includes(o) ? (rt.joins(o) ? 0 : 1) : 2) * 2 + (can(o, role) ? 0 : 1);
      return rt.order().map(id => rt.all[id]).filter(o => o && o !== me && possible(o.design, role))
        .map((o, i) => [o.design.id, rank(o), i]).sort((a, b) => a[1] - b[1] || a[2] - b[2]).map(([id]) => id);
    }
    if (ownIdle(me.design).includes(key)) {
      const friends = (ability(key)?.partners || []).filter(id => id !== me.design.id && rt.known(id));
      return [...friends.filter(onScreen), ...friends.filter(id => !onScreen(id))];
    }
    return [];
  }
  const partners = (id, key) => (ROLES[key] ? candidates(rt.companion(id), key) : []);
  const partnerOf = (me, key, away = new Set()) => candidates(me, key).find(id => !away.has(id)) ?? null;

  // ---------- the moves ----------
  const out = async g => { if (g.me.atHome) await acts.comeOut(g); };
  const to = (g, spot, how) => (spot ? travelTo(spot, g, how) : null);
  const floorSpot = () => ({ ledge: FLOOR, dx: rand(20 * env.S, castleLeft() - 20 * env.S), rest: 'stand' });
  // Somewhere high: an edge well above it, or any edge.
  const highSpot = me => acts.chooseSpot(me, { only: 'edge', filter: l => { const q = l.geom(); return q && q.y < me.y - 30 * me.s; } })
    || acts.chooseSpot(me, { only: 'edge' });
  // Up somewhere high its own way (or its world's way up), so it can show a way down.
  const upWay = me => ownTravel(me.design).find(w => !ability(w)?.down) || generalWay(me, 'up', w => possible(me.design, w)) || 'hop';
  async function upThenDown(g, way) {
    if (g.me.y > floorY() - 30 * g.me.s) await to(g, highSpot(g.me), upWay(g.me));
    await to(g, floorSpot(), way);
  }
  // Another surface (for an ender pearl or a grappling hook).
  const elsewhere = me => acts.chooseSpot(me, { only: 'edge', filter: l => l.id !== baseOf(me.anchor || { ledge: FLOOR }).id }) || floorSpot();
  // Room above (a portal is 5 blocks tall, a wormhole a bit taller than the companion): up high
  // when it's on the floor.
  const roomySpot = me => {
    const onFloor = me.anchor && baseOf(me.anchor) === FLOOR;
    return (onFloor && acts.chooseSpot(me, { only: 'edge', filter: l => { const q = l.geom(); return q && q.y > 22 * me.s; } })) || floorSpot();
  };
  // A pest of a kind turns up beside it.
  const pest = type => g => (g.me.anchor ? acts.pestFight(g, g.me.anchor, type) : null);
  // A nap of NAP ms, its own way (a click wakes it, as always).
  async function nap(g, bed) {
    const me = g.me, until = clockNow() + NAP;
    const awake = () => { if (me.wakeRequest) { me.wakeRequest = false; return true; } return clockNow() > until; };
    await acts.sleep(g, awake, { minMs: 3000, bed });
  }
  // One dance move on its own for two bars, to a beat only it hears.
  async function soloDance(g, move) {
    const t0 = clockNow(), beatMs = 60000 / together.BPM;
    const beat = { host: null, t0, beatMs, on: true, live: () => true,
      beat: now => { const k = Math.max(0, now - t0); return { n: Math.floor(k / beatMs), f: (k % beatMs) / beatMs }; } };
    await together.dance(g, beat, 8, move);
  }
  const STEPS = {
    near: g => acts.goNear(g),
    far: g => to(g, acts.spots.far(g.me)),
    elytra: g => to(g, highSpot(g.me), 'elytra'),
    jetpack: g => to(g, highSpot(g.me), 'jetpack'),
    parachute: g => upThenDown(g, 'parachute'),
    slime: g => upThenDown(g, 'slime'),
    pearl: g => to(g, elsewhere(g.me), 'pearl'),
    hook: g => to(g, elsewhere(g.me), 'hook'),
    portal: g => to(g, roomySpot(g.me), 'portal'),
    wormhole: g => to(g, roomySpot(g.me), 'wormhole'),
    endermite: pest('endermite'),
    'pests:bug': pest('bug'),
    'pests:glitch': pest('glitch'),
    async lounge(g) {
      const me = g.me, a = me.anchor;
      if (!a || a.ledge === FLOOR || a.rest === 'lean' || a.ledge.kind === 'text') {
        await to(g, acts.chooseSpot(me, { only: 'edge', reachable: true }) || acts.chooseSpot(me, { only: 'edge' }));
      }
      if (me.anchor) { me.anchor.rest = 'lounge'; applyRest(me, me.anchor); }
    },
    lean: g => to(g, acts.chooseSpot(g.me, { only: 'lean' })),
    text: g => to(g, acts.chooseSpot(g.me, { only: 'text' })),
    sunglasses: g => acts.sunglasses(g),
    chair: g => acts.chairBreak(g),
    think: g => acts.think(g),
    async babies(g) { await acts.spawnBabies(g); await acts.goNear(g); },
    pointer: g => playWithPointer(g, { shown: true }),   // with wherever the pointer is (pointer.js)
    nap: g => nap(g, false),
    bed: g => nap(g, true),
  };
  // What it does for a key that it plays alone: one of the above, its own way to travel (one that
  // only goes down goes up first), its own thing, a dance move.
  function stepOf(me, key) {
    if (key.startsWith('dance.')) return g => soloDance(g, key.slice('dance.'.length));
    if (ownTravel(me.design).includes(key)) return ability(key)?.down ? g => upThenDown(g, key) : g => to(g, acts.spots.far(g.me), key);
    if (ownIdle(me.design).includes(key)) return g => ability(key).idle(g);
    return STEPS[key];
  }

  // Starts it: its runs (the runtime's), with the partner where it has one.
  function start(me, key, partner) {
    const both = script => async (gA, gB) => { await Promise.all([out(gA), out(gB)]); await script(gA, gB); };
    switch (key) {
      case 'home': return rt.run(me, g => acts.goHome(g));
      case 'out': return rt.run(me, async g => { if (!g.me.atHome) await acts.goHome(g); await acts.comeOut(g); });
      case 'fight.duel': return rt.runPair(me, partner, both((gA, gB) => fights.duel(gA, gB)));
      case 'fight.evil': return rt.turnEvil(me);
      case 'party.host': return rt.startParty(me);
      case 'basketball.throw': return rt.runPair(me, partner, both(together.basketball));
      case 'basketball.catch': return rt.runPair(partner, me, both(together.basketball));
      case 'party.join':
        // The partner throws it; this one waits for the beat and dances along, its switch or not.
        rt.startParty(partner);
        return rt.run(me, async g => {
          await out(g);
          const t0 = clockNow();
          while (together.partyOn()?.host !== partner) {
            if (clockNow() - t0 > 30000 || partner.idle || !rt.cast.includes(partner)) return;   // the party didn't happen
            await tick(g);
          }
          await together.danceAlong(g, together.partyOn());
        });
      default: {
        const step = stepOf(me, key);
        // An own thing with a friend: the friend stops what it's doing and waits for it.
        if (partner) rt.run(partner, async () => {});
        return rt.run(me, async g => {
          await out(g);
          if (partner) { const t0 = clockNow(); while (!rt.free(partner) && clockNow() - t0 < PARTNER_WAIT) await tick(g); }
          await step(g);
        });
      }
    }
  }

  // ---------- playing one ----------
  // Over once the companion is back to resting (an evil one: also itself again), when it left the
  // screen, or when another Show me took it over.
  const over = session => () => {
    const { me, key } = session;
    if (sessions.get(me) !== session || !rt.cast.includes(me)) return true;
    return me.idle && !(key === 'fight.evil' && me.evil);
  };

  async function run(id, key, { with: chosen = null, elsewhere = [], puff = false } = {}) {
    if (rt.destroyed()) return false;
    if (!rt.known(id)) throw new Error('Unknown companion: ' + id);
    // `elsewhere`: on screen in another runtime (the desktop's other monitors), so not a visitor here.
    const away = new Set(elsewhere);
    if (away.has(id)) return false;
    await rt.ready([id]);                                      // its design and moves (a host that brings them later)
    if (rt.destroyed() || !rt.started()) return false;
    const me = rt.all[id];
    if (!has(me, key)) return false;
    let partnerId = null;
    if (chosen != null) {
      if (away.has(chosen) || !candidates(me, key).includes(chosen)) return false;
      partnerId = chosen;
    } else partnerId = partnerOf(me, key, away);
    if (ROLES[key] && !partnerId) return false;               // a duel needs someone to fight
    if (partnerId) await rt.ready([partnerId]);
    if (rt.destroyed() || !rt.started()) return false;
    const partner = partnerId ? rt.all[partnerId] : null;

    const session = { me, key, members: new Set([me, partner].filter(Boolean)) };
    for (const m of session.members) sessions.set(m, session);
    for (const m of session.members) if (!rt.cast.includes(m)) rt.visit(m);
    rt.emit('showMe', { id, key, playing: true });
    start(me, key, partner);
    await frameUntil(over(session), LONGEST);
    // Back to its own life (or a visitor leaves): those resting now; the others once they are.
    for (const m of session.members) {
      if (sessions.get(m) !== session) continue;
      sessions.delete(m);
      if (rt.destroyed() || !rt.cast.includes(m)) continue;
      if (puff && rt.visiting(m)) rt.dismiss(m, { puff: true });
      else if (m.idle) rt.run(m, async () => {});
    }
    rt.emit('showMe', { id, key, playing: false });
    return true;
  }

  // Waits for cond() on the runtime's frames, at most ms; false when it ran out of time or the
  // runtime went away.
  function frameUntil(cond, ms) {
    return new Promise(resolve => {
      if (cond()) { resolve(true); return; }
      const t0 = clockNow(), w = {
        check(now) { if (cond()) w.done(true); else if (now - t0 > ms) w.done(false); },
        done(value) { waiting.delete(w); resolve(value); },
      };
      waiting.add(w);
    });
  }

  // A visitor whose Show me is over goes again: off over the nearer side, its own way (from home
  // it simply goes, out of sight already), and is then off screen without a trace.
  async function leave(g) {
    const me = g.me;
    if (!me.atHome) await leaveOver(g, me.x < env.W / 2 ? 'left' : 'right');
    rt.dismiss(me);
  }

  return {
    api: { list, partners, partner: (id, key) => partnerOf(rt.companion(id), key), run },
    of: me => sessions.get(me) || null,                    // the Show me a companion is in, if any
    leave,
    frame(now) { for (const w of [...waiting]) w.check(now); },
    dispose() { for (const w of [...waiting]) w.done(false); sessions.clear(); },
  };
}
